import { put, select, call } from 'redux-saga/effects'
import { replace, push } from 'connected-react-router'
import AuthActions from '../store/authRedux'
import AccountDataActions from '../store/accountDataRedux'
import ClientActions from '../store/clientRedux'
import ProductActions from '../store/productRedux'
import TicketActions from '../store/ticketRedux'
import LayoutActions from '../store/layoutRedux'
import LinkConfigActions from '../store/linkConfigRedux'
import auth from '../services/auth/AuthProvider';
import accountApi from '../services/api/accountDataAPI'
import { Permissions } from '../app/services/Permissions'
import settingsUtil from '../services/util/SettingsUtil'
import { Settings } from '../services/util/Settings'

export const getAccessToken = state => state.auth.accessToken
export const getLogin = state => state.login
export const getConfiguration = state => state.configuration;
export const getLanguage = state => state.layout.lang
export const getRouterPath = state => state.router.location.pathname
export const getRegisterStep = state => state.register.step
export const getPermissions = state => state.accountData.permissions
const api = accountApi.create();
export function* loginUser() {
    var config = yield select(getConfiguration)
    if (config !== null) {
        yield call(auth.initProvider, config.configuration)
    }
    var lang = yield select(getLanguage)
    yield call(auth.login, lang);
}

export function* restoreUserSession() {
    // VAI-1119/1192 If a user is coming onto the page with a valid token
    // (i.e. from another HRB app, or a new browser tab), we need to restore some
    // basic session information.
    var profile = yield select(auth.getProfile);
    if (profile) {
        try {
            var employeeId = profile.employeeId ? Number(profile.employeeId) : null;

            // Set up account data retrieved from CAS and redirect to home page.
            yield put(AccountDataActions.setEmployeeId(employeeId))
            yield put(AccountDataActions.setFirstName(profile.givenName))
            yield put(AuthActions.setSignedIn(true))

            var savedLang = settingsUtil.getSetting(employeeId, Settings.LANG)
            if (savedLang && employeeId) {
                yield put(LayoutActions.setLanguage(employeeId, savedLang))
            }

            if (!auth.isAuthenticated()) {
                // user isn't logged in ... go away
                yield put(push('/logout'));
            }

            var perms = yield select(getPermissions) || [];
            if (!perms || perms.length === 0) {
                var accountResponse = yield call(api.getAccountInformation);
                if (accountResponse.ok) {
                    perms = accountResponse.data.permissions || [];
                    yield put(AccountDataActions.setPermissions(accountResponse.data.permissions))
                }
            }
            
            var canAccessApp = perms.includes(Permissions.ACCESS_APPLICATION);
            if (!canAccessApp) {
                // logged in user does not have the proper role.
                yield put(push('/unauthorized'))
            }
        } catch (ex) {
            console.log(ex)
        } finally {
            // no matter what happens, stop spinning
            yield put(LayoutActions.setPageLoading(false))
        }
    }
}

export function* navigateToDashboard() {
    // this is called when login is completed, if the path is '/' redirect the user to dashboard
    var path = yield select(getRouterPath)

    if (!path.startsWith('/callback')) {
        yield put(replace('/dashboard'))
    }
}

function handleEmpty(val) {
    return (!val || (Array.isArray(val) && val.length === 0)) ? null : val
}

export function* handleLoginCallback() {
    try {
        var config = yield select(getConfiguration);
        yield call(auth.initProvider, config.configuration);

        var response = yield call(auth.handleAuthentication);
        if (!auth.isAuthenticated()) {
            yield put(AuthActions.setLoginCallbackStatus('failed'));
            yield put(LayoutActions.setPageLoading(false));
            return;
        }

        var profile = yield select(auth.getProfile);
        if (!profile) {
            yield put(AuthActions.setLoginCallbackStatus('failed'));
            yield put(LayoutActions.setPageLoading(false));
            return;
        }
        
        var employeeId = profile.employeeId ? Number(profile.employeeId) : null;

        yield put(AccountDataActions.setEmployeeId(employeeId));
        yield put(AccountDataActions.setFirstName(profile.givenName));
        yield put(AuthActions.setSignedIn(true));
        yield put(AuthActions.finalizeLogin());
        yield put(AuthActions.setLoginCallbackStatus(null));

        var savedLang = settingsUtil.getSetting(employeeId, Settings.LANG)
        if (employeeId) {
            yield put(LayoutActions.setLanguage(employeeId, savedLang || response.lang))
        }

        var accountResponse = yield call(api.getAccountInformation);
        var permissions = [];
        if (accountResponse.ok) {
            var employeeId = Number(accountResponse.data.employeeId || null);
            if(employeeId) {
                yield put(AccountDataActions.setEmployeeId(employeeId));
            }
            permissions = accountResponse.data.permissions || [];
            yield put(AccountDataActions.setPermissions(permissions));
        }

        if (!permissions.includes(Permissions.ACCESS_APPLICATION)) {
            yield put(push('/unauthorized'));
            yield put(LayoutActions.setPageLoading(false));
            return;
        } 
        
        // VAI-1085 moved this here to prevent double refresh on callback from login
        let originalPath = localStorage.getItem('spark_original_pathname');
        if ((originalPath === null) || (originalPath === '')) {
            originalPath = '/home';
        }
        yield put(push(originalPath));
        yield put(LayoutActions.setPageLoading(false));
    }
    catch (ex) {
        yield put(AuthActions.setLoginCallbackStatus('failed'));
        yield put(LayoutActions.setPageLoading(false));
    }    
}


export function* logoutUser() {
    yield call(auth.logout)
}

export function* finalizeLogout() {
    if (typeof window.heap?.resetIdentity === 'function') {
        window.heap.resetIdentity();
    }

    // VAI-952, we are currently calling this post-logout (rather than ON logout)
    // to prevent the UI from flickering to blank data before hitting the logout page.
    // in the future, this should be happening as soon as logout is clicked. Refer to hrbcava 
    // to see how we handle a myriad of pre-logout actions, and then do one finalizeLogout when
    // they are complete.

    yield put(AuthActions.setSignedIn(false))

    yield put(AccountDataActions.resetAccount())
    yield put(AuthActions.clearToken())
    yield put(ClientActions.resetClients())
    yield put(LayoutActions.resetLayout())
    yield put(LinkConfigActions.resetLink())
    yield put(ProductActions.resetProducts())
    yield put(TicketActions.resetTickets())
}